import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Camera, ChevronDown, ChevronRight, ExternalLink, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ConfidenceBadge = ({ confidence }) => {
    if (confidence == null) return <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-600">Matched manually</span>;
    const isHigh = confidence >= 80;
    const isMedium = confidence >= 60 && confidence < 80;
    return (
        <span className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
            isHigh ? 'bg-emerald-50 text-emerald-700' :
            isMedium ? 'bg-amber-50 text-amber-700' :
            'bg-stone-100 text-stone-600'
        }`}>
            {confidence.toFixed(0)}%
        </span>
    );
};

export default function ContactEventPhotos({ contactId }) {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(true);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        if (!contactId) return;
        let cancelled = false;

        const fetchPhotos = async () => {
            try {
                const res = await axios.get(`${API}/events/contacts/${contactId}/event-photos`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!cancelled) {
                    const data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                    setPhotos(data);
                    setExpanded(data.length > 0);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Failed to load event photos', err);
                    setPhotos([]);
                    setExpanded(false);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchPhotos();
        return () => { cancelled = true; };
    }, [contactId, token]);

    return (
        <>
            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                {/* Header */}
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="w-full px-5 py-3 border-b border-stone-100 flex items-center justify-between hover:bg-stone-50 transition-colors"
                >
                    <div className="flex items-center gap-2">
                        <Camera className="w-4 h-4 text-stone-500" />
                        <h3 className="text-sm font-semibold text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Event Photos
                        </h3>
                        {!loading && photos.length > 0 && (
                            <span className="text-[10px] font-medium bg-stone-100 text-stone-600 px-1.5 py-0.5 rounded-full">
                                {photos.length}
                            </span>
                        )}
                    </div>
                    {expanded ? (
                        <ChevronDown className="w-4 h-4 text-stone-400" />
                    ) : (
                        <ChevronRight className="w-4 h-4 text-stone-400" />
                    )}
                </button>

                {/* Body */}
                {expanded && (
                    <div className="px-5 py-3">
                        {loading ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 className="w-4 h-4 text-stone-400 animate-spin" />
                            </div>
                        ) : photos.length === 0 ? (
                            <p className="text-xs text-stone-400 py-1">No event photos tagged for this contact</p>
                        ) : (
                            <div className="flex gap-3 overflow-x-auto pb-2">
                                {photos.map((photo) => (
                                    <div
                                        key={photo.match_id}
                                        className="flex-shrink-0 w-40 cursor-pointer group"
                                        onClick={() => setLightbox(photo)}
                                    >
                                        <div className="w-40 h-40 rounded-lg overflow-hidden bg-stone-100">
                                            <img
                                                src={photo.photo_url || photo.download_url}
                                                alt={photo.photo_display_filename}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                            />
                                        </div>
                                        <div className="mt-1.5 space-y-0.5">
                                            <p className="text-xs font-medium text-stone-700 truncate">
                                                {photo.event_name}
                                            </p>
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-stone-400">
                                                    {formatDate(photo.event_date)}
                                                </span>
                                                <ConfidenceBadge confidence={photo.confidence} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightbox && createPortal(
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
                    onClick={() => setLightbox(null)}
                >
                    <div
                        className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <button
                            onClick={() => setLightbox(null)}
                            className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        {/* Photo */}
                        <div className="bg-stone-100">
                            <img
                                src={lightbox.photo_url}
                                alt={lightbox.photo_display_filename}
                                className="w-full max-h-[60vh] object-contain"
                            />
                        </div>

                        {/* Info */}
                        <div className="px-5 py-4 flex items-center justify-between">
                            <div>
                                <h4 className="text-sm font-semibold text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {lightbox.event_name}
                                </h4>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-xs text-stone-500">
                                        {formatDate(lightbox.event_date)}
                                    </span>
                                    <ConfidenceBadge confidence={lightbox.confidence} />
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setLightbox(null);
                                    navigate(`/events/${lightbox.event_id}`);
                                }}
                                className="flex items-center gap-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 px-3 py-1.5 rounded-lg border border-stone-200 hover:border-stone-300 transition-colors"
                            >
                                View Event
                                <ExternalLink className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
